import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { TrendUp, ChartBar, Users, ClipboardText, Trophy, Warning } from "@phosphor-icons/react";

/** Phase 6D — Unified Analytics dashboard.
 *  Sources /api/analytics/overview and renders the 10 required widgets.
 */
const PERIODS = ["day", "week", "month", "quarter", "year"];

const Kpi = ({ label, value, hint, tone = "slate", testid }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid={testid}>
    <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
    <div className={`text-2xl font-semibold mt-1 text-${tone}-800`}>{value}</div>
    {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
  </div>
);

function Bar({ label, value, max, testid }) {
  const pct = Math.min(100, (value / Math.max(1, max)) * 100);
  return (
    <div className="text-xs" data-testid={testid}>
      <div className="flex justify-between mb-0.5"><span>{label}</span><span className="tabular-nums">{value}</span></div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

export default function Analytics() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/analytics/overview?period=${period}`);
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load analytics"); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  if (loading || !data) return <div className="text-sm text-slate-400 py-10 text-center" data-testid="analytics-loading">Loading…</div>;

  const k = data.kpis || {};
  const funnelMax = Math.max(1, ...data.conversion_funnel.map((f) => f.count));
  const wdMax = Math.max(1, ...(data.workload_distribution || []).map((w) => w.max));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Insights</div>
          <h2 className="text-2xl font-semibold">Analytics</h2>
          <p className="text-sm text-slate-500">Real-time KPIs · trends · funnel · workload · turnaround.</p>
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
                    data-testid={`period-${p}`}
                    className={`text-sm px-3 py-1.5 rounded-md border capitalize ${period === p ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* 1. KPIs */}
      <section data-testid="analytics-kpis">
        <h3 className="text-sm font-semibold text-slate-700 mb-2">Real-time KPIs</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <Kpi label="Total candidates" value={k.total_candidates} tone="sky" testid="kpi-total-candidates" />
          <Kpi label="Active" value={k.active_candidates} tone="emerald" testid="kpi-active-candidates" />
          <Kpi label="Applications" value={k.applications} tone="violet" testid="kpi-applications" hint={`since ${data.since}`} />
          <Kpi label="Interviews" value={k.interviews} tone="amber" testid="kpi-interviews" />
          <Kpi label="Offers" value={k.offers} tone="emerald" testid="kpi-offers" />
          <Kpi label="Placements" value={k.placements} tone="sky" testid="kpi-placements" />
          <Kpi label="Joinings" value={k.joinings} tone="emerald" testid="kpi-joinings" />
          <Kpi label="Offer→Join %" value={`${k.offer_to_joining_ratio_pct ?? 0}%`} tone="violet" testid="kpi-offer-to-joining" />
          <Kpi label="Placement success %" value={`${k.placement_success_pct ?? 0}%`} tone="amber" testid="kpi-placement-success" />
        </div>
      </section>

      {/* 2 + 3. Trends + Funnel */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="analytics-trends">
          <div className="flex items-center gap-2 mb-3"><TrendUp size={14} /><h4 className="text-sm font-semibold">Applications trend</h4></div>
          <div className="space-y-3">
            {["day", "week", "month"].map((k2) => {
              const trend = (data.trends || {})[k2] || [];
              const max = Math.max(1, ...trend.map((t) => t.count));
              return (
                <div key={k2} data-testid={`trend-${k2}`}>
                  <div className="text-xs text-slate-500 mb-1 capitalize">{k2}</div>
                  <div className="flex items-end gap-1 h-16">
                    {trend.map((t) => (
                      <div key={t.bucket} className="flex-1 min-w-[6px] bg-sky-500 rounded-t hover:bg-sky-600 relative group"
                           style={{ height: `${(t.count / max) * 100}%` }}
                           title={`${t.bucket}: ${t.count}`} />
                    ))}
                    {trend.length === 0 && <div className="text-xs text-slate-400">No data</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="analytics-funnel">
          <div className="flex items-center gap-2 mb-3"><ChartBar size={14} /><h4 className="text-sm font-semibold">Conversion funnel</h4></div>
          <div className="space-y-2">
            {data.conversion_funnel.map((f) => (
              <Bar key={f.stage} label={f.stage} value={f.count} max={funnelMax}
                   testid={`funnel-${f.stage.toLowerCase()}`} />
            ))}
          </div>
        </div>
      </section>

      {/* 4. Top executives + 5. Top team leaders */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="analytics-top-executives">
          <div className="flex items-center gap-2 mb-3"><Trophy size={14} /><h4 className="text-sm font-semibold">Top executives</h4></div>
          <ol className="space-y-1 text-sm">
            {(data.top_executives || []).map((r, i) => (
              <li key={r.employee} className="flex justify-between items-center border-b border-slate-100 py-1" data-testid={`exec-rank-${i}`}>
                <span>{i + 1}. {r.employee} <span className="text-xs text-slate-400">{r.team || "—"}</span></span>
                <span className="text-xs text-slate-500 tabular-nums">
                  {r.applications} apps · {r.offers} off · {r.placements} pl · <b>{r.score}</b>
                </span>
              </li>
            ))}
            {(data.top_executives || []).length === 0 && <li className="text-xs text-slate-400 py-2">No data</li>}
          </ol>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="analytics-top-team-leaders">
          <div className="flex items-center gap-2 mb-3"><Users size={14} /><h4 className="text-sm font-semibold">Top managers</h4></div>
          <ol className="space-y-1 text-sm">
            {(data.top_team_leaders || []).map((r, i) => (
              <li key={r.team_leader} className="flex justify-between items-center border-b border-slate-100 py-1" data-testid={`tl-rank-${i}`}>
                <span>{i + 1}. {r.team_leader} <span className="text-xs text-slate-400">· {r.team}</span></span>
                <span className="text-xs text-slate-500 tabular-nums">
                  {r.members} mem · {r.placements} pl
                </span>
              </li>
            ))}
            {(data.top_team_leaders || []).length === 0 && <li className="text-xs text-slate-400 py-2">No data</li>}
          </ol>
        </div>
      </section>

      {/* 6. HR turnaround + 7. Candidate pipeline */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="analytics-hr-turnaround">
          <div className="flex items-center gap-2 mb-3"><ClipboardText size={14} /><h4 className="text-sm font-semibold">HR turnaround</h4></div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500"><tr><th className="text-left">HR</th><th>Approved</th><th>Sent back</th><th>Avg hours</th></tr></thead>
            <tbody>
              {(data.hr_turnaround || []).map((r) => (
                <tr key={r.hr_user} className="border-t border-slate-100" data-testid={`hr-row-${r.hr_code}`}>
                  <td className="py-1">{r.hr_user}</td>
                  <td className="text-center tabular-nums">{r.approved}</td>
                  <td className="text-center tabular-nums">{r.sent_back}</td>
                  <td className="text-center tabular-nums">{r.avg_turnaround_hours}h</td>
                </tr>
              ))}
              {(data.hr_turnaround || []).length === 0 && <tr><td colSpan={4} className="text-xs text-slate-400 py-2 text-center">No data</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="analytics-candidate-pipeline">
          <div className="flex items-center gap-2 mb-3"><Warning size={14} /><h4 className="text-sm font-semibold">Candidate pipeline</h4></div>
          <div className="space-y-1">
            {(data.candidate_pipeline || []).map((r) => (
              <Bar key={r.status} label={`${r.status} (${r.percent}%)`} value={r.count}
                   max={Math.max(1, ...(data.candidate_pipeline || []).map((x) => x.count))}
                   testid={`pipeline-${r.status.replace(/\s/g, "-").toLowerCase()}`} />
            ))}
          </div>
        </div>
      </section>

      {/* 8. Workload distribution */}
      <section className="bg-white border border-slate-200 rounded-lg p-4" data-testid="analytics-workload">
        <div className="flex items-center gap-2 mb-3"><Users size={14} /><h4 className="text-sm font-semibold">Workload distribution (top 20)</h4></div>
        <div className="space-y-1">
          {(data.workload_distribution || []).map((w) => (
            <div key={w.employee} className="flex items-center gap-3 text-sm" data-testid={`wl-${w.code}`}>
              <div className="w-48 truncate">
                <div className="font-medium">{w.employee}</div>
                <div className="text-[10px] text-slate-400">{w.code} · {w.team || "—"}</div>
              </div>
              <div className="flex-1 bg-slate-100 h-2 rounded-full overflow-hidden">
                <div className={`h-full ${w.utilization_pct >= 100 ? "bg-red-500" : w.utilization_pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                     style={{ width: `${Math.min(100, w.utilization_pct)}%` }} />
              </div>
              <div className="w-28 text-right text-xs text-slate-500 tabular-nums">
                {w.load}/{w.max} · {w.utilization_pct}%
              </div>
            </div>
          ))}
          {(data.workload_distribution || []).length === 0 && <div className="text-xs text-slate-400">No data</div>}
        </div>
      </section>

      <div className="text-[10px] text-slate-400 text-right" data-testid="analytics-generated-at">
        Generated at {new Date(data.generated_at).toLocaleString()}
      </div>
    </div>
  );
}
